import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  TextField,
  Button,
  useMediaQuery,
  Snackbar,
  Alert,
  Slide,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import AnseraLogo from "assets/images/Anser.png";
import axios from "axios";
import { FAST_API_BASE_URL } from "constants";
import { useAuth } from "context/AuthContext";
import { useSystemTheme } from "context/SystemThemeContext";

// Move StyledTextField outside of the component to prevent re-creation
const StyledTextField = ({ isDarkMode, name, value, onChange, multiline, rows, placeholder }) => {
  return (
    <TextField
      name={name}
      value={value || ""}
      onChange={onChange}
      multiline={multiline}
      rows={rows}
      placeholder={placeholder}
      fullWidth
      size={multiline ? undefined : "small"}
      InputProps={{
        sx: {
          backgroundColor: isDarkMode ? "#060C18 !important" : "#ffffff !important",
          color: isDarkMode ? "#e2e8f0 !important" : "#344767 !important",
          borderRadius: "8px",
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: isDarkMode ? "#4a5568 !important" : "#d2d6da !important",
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: "#219297 !important",
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: "#219297 !important",
            borderWidth: "2px !important",
          },
        },
      }}
      InputLabelProps={{
        sx: {
          color: isDarkMode ? "#a0aec0 !important" : undefined,
          '&.Mui-focused': {
            color: "#219297 !important",
          },
        },
      }}
      sx={{
        '& .MuiInputBase-input': {
          color: isDarkMode ? "#e2e8f0 !important" : "#344767 !important",
          '&::placeholder': {
            color: isDarkMode ? "#a0aec0 !important" : "#9e9e9e !important",
            opacity: 1,
          },
        },
        '& .MuiInputBase-multiline': {
          padding: "12px 14px",
        },
      }}
    />
  );
};

const EmailTemplate = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { mode } = useSystemTheme();
  const isDarkMode = mode === "dark";
  
  const [form, setForm] = useState({
    emailPlaceholder: "",
    emailHeader: "",
    emailFooter: "",
  });
  const [initialSettings, setInitialSettings] = useState({
    emailPlaceholder: "",  
    emailHeader: "",
    emailFooter: "",  
  });

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const { currentDomain } = useAuth();
  const domainId = currentDomain?.domain_token;

  useEffect(() => {
    const getSettings = async () => {
      try {
        const res = await axios.get(`${FAST_API_BASE_URL}/api/settings`, {
          headers: {
            'Content-Type': 'application/json',
            'DOMAIN-TOKEN': domainId,
          },  
        });
        const settings = res.data.result.ansera_search_chat_curl[0];
        setForm({
          emailPlaceholder: settings.ansera_search_email_placeholder || "",
          emailHeader: settings.email_header || "",
          emailFooter: settings.email_footer || "",
        });
        setInitialSettings({
          emailPlaceholder: settings.ansera_search_email_placeholder || "",
          emailHeader: settings.email_header || "",
          emailFooter: settings.email_footer || "",
        });
      } catch (err) {
        console.error("Error fetching settings:", err);
      }
    }
    getSettings();
  }, [domainId]);

  // Memoize handleChange to prevent recreation
  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleSave = async () => {
    if (!initialSettings) {
      return null;
    }
    const payload = {};
    if (form.emailPlaceholder !== initialSettings.emailPlaceholder) {
      payload.ansera_search_email_placeholder = form.emailPlaceholder;
    } 
    if (form.emailHeader !== initialSettings.emailHeader) {
      payload.email_header = form.emailHeader;
    }
    if (form.emailFooter !== initialSettings.emailFooter) {
      payload.email_footer = form.emailFooter;
    }

    try {
      if (Object.keys(payload).length > 0) {
        await axios.patch(`${FAST_API_BASE_URL}/api/settings`, payload, {
          headers: {
            'Content-Type': 'application/json',
            'DOMAIN-TOKEN': domainId,
          },
        });
      }
      setSnackbarMessage("Settings updated successfully!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      setInitialSettings({ ...form });
    } catch (error) {
      console.error("Failed to save settings:", error);
      setSnackbarMessage("Failed to save Settings!");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  }

  const hasChanges = JSON.stringify(form) !== JSON.stringify(initialSettings);

  const renderRow = useCallback((label, field) => (
    <SoftBox
      display="flex"
      flexDirection={isMobile ? "column" : "row"}
      gap={2}
      alignItems={isMobile ? "flex-start" : "center"}
      mb={1.5}
    >
      <SoftTypography
        width={isMobile ? "100%" : "240px"}
        fontSize="0.875rem"
        fontWeight="medium"
        sx={{ color: isDarkMode ? "#e2e8f0" : undefined }}
      >
        {label}
      </SoftTypography>
      <SoftBox flex={1}>{field}</SoftBox>
    </SoftBox>
  ), [isMobile, isDarkMode]);
  
  return (
    <SoftBox
      sx={{
        backgroundColor: isDarkMode ? "#060C18" : "#ffffff",
        p: isMobile ? 2 : 4,
        borderRadius: "16px",
        boxShadow: isDarkMode 
          ? "0px 6px 20px rgba(0, 0, 0, 0.3)" 
          : "0px 6px 20px rgba(0, 0, 0, 0.06)",
        transition: "background-color 0.3s ease, box-shadow 0.3s ease",
      }}
      display="flex"
      flexDirection="column"
      gap={2.5}
    >
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        TransitionComponent={(props) => <Slide {...props} direction="top" />}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          variant="filled"
          sx={{
            width: "100%",
            color: "#ffffff",
            backgroundColor:
              snackbarSeverity === "success"
                ? "#008080" 
                : snackbarSeverity === "error"
                ? "#d32f2f"
                : undefined, 
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      <SoftBox display="flex" alignItems="center" gap={1}>
        <img 
          src={AnseraLogo} 
          alt="Ansera Logo" 
          width={64}
          style={{
            filter: isDarkMode ? "brightness(0) invert(1)" : "none",
            transition: "filter 0.3s ease",
          }}
        />
        <SoftTypography 
          variant="h6" 
          fontWeight="bold"
          sx={{ color: isDarkMode ? "#e2e8f0" : undefined }}
        >
          Email Template
        </SoftTypography>
      </SoftBox>

      <SoftTypography
        variant="body2"
        sx={{
          borderBottom: `1px solid ${isDarkMode ? "#2d2d3f" : "#e0e0e0"}`,
          pb: 1,
          color: isDarkMode ? "#a0aec0" : "text.secondary",
          transition: "border-color 0.3s ease",
        }}
      >
        Personalize the email that sends chat history to your users.
      </SoftTypography>

      {renderRow(
        "Email Input Placeholder",
        <StyledTextField
          isDarkMode={isDarkMode}
          name="emailPlaceholder"
          value={form.emailPlaceholder}
          onChange={handleChange}
          placeholder="Enter email address"
        />
      )}

      {renderRow(
        "Email Template Header",
        <StyledTextField
          isDarkMode={isDarkMode}
          name="emailHeader"
          value={form.emailHeader}
          onChange={handleChange}
          multiline
          rows={3}
          placeholder="Hi,"
        />
      )}

      {renderRow(
        "Email Template Footer",
        <StyledTextField
          isDarkMode={isDarkMode}
          name="emailFooter"
          value={form.emailFooter}
          onChange={handleChange}
          multiline
          rows={3}
          placeholder="Thank You"
        />
      )}

      <SoftBox mt={1}>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!hasChanges}
          sx={{
            backgroundColor: "#219297",
            color: "#fff",
            textTransform: "none",
            fontSize: "0.875rem",
            fontWeight: 600,
            px: 3,
            py: 1,
            borderRadius: "8px",
            "&:hover": {
              backgroundColor: "#1a767a",
            },
            "&:disabled": {
              backgroundColor: isDarkMode ? "#2d2d3f" : "#cccccc",
              color: isDarkMode ? "#a0aec0" : "#666666",
            },
          }}
        >
          Save Changes
        </Button>
      </SoftBox>
    </SoftBox>
  );  
};  

export default EmailTemplate;